import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LogOut, Calendar, Clock, CheckSquare, MessageSquare, MapPin, Package, TrendingUp, User, Plus, FileText, Upload, HelpCircle, RefreshCw, Award, CreditCard, ShieldCheck, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MaterialOrderForm from '@/components/MaterialOrderForm';
import { ProjectCombobox } from '@/components/dashboard/ProjectCombobox';
import ChangePasswordDialog from '@/components/employee/ChangePasswordDialog';
import ImageViewerDialog from '@/components/employee/ImageViewerDialog';
import WorkProgressDialog from '@/components/employee/WorkProgressDialog';
import { hasFeature } from '@/utils/features';
import { PushNotificationManager } from '@/components/PushNotificationManager';
import { NotificationCenter } from '@/components/NotificationCenter';
import { PWAInstallBanner } from '@/components/PWAInstallBanner';
import { IOSInstallBanner } from '@/components/IOSInstallBanner';
import { PersonalDetailsDialog } from '@/components/PersonalDetailsDialog';
import { DrivingLicensePrompt } from '@/components/DrivingLicensePrompt';
import { OnboardingTour } from '@/components/OnboardingTour';
import { HelpCenter } from '@/components/HelpCenter';
import { forceAppUpdate } from '@/lib/appUpdate';
import { ProjectProgressReporter } from '@/components/ProjectProgressReporter';
import { MyEarnings } from '@/components/MyEarnings';
import { EmployeeDaysBreakdown } from '@/components/dashboard/StatsDetailsDialog';
import { MyTasksCard } from '@/components/MyTasksCard';

// Shared Styles
import '@/styles/companyHeader.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmployeeDashboard({ user, setUser }) {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [todaySchedule, setTodaySchedule] = useState(null);
  const [todayProjects, setTodayProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectSelect, setShowProjectSelect] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [messages, setMessages] = useState([]);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationPermission, setLocationPermission] = useState('checking');
  const [hoursToday, setHoursToday] = useState(0);
  const [activeTimeEntry, setActiveTimeEntry] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [materialOrders, setMaterialOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]); // All active projects for transfers
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Proximity tracking for auto clock-out
  const [locationWatchId, setLocationWatchId] = useState(null);
  const [proximityWarning, setProximityWarning] = useState(false);
  const GEOFENCE_RADIUS = 150; // meters
  
  // Work Progress Dialog
  const [showWorkProgressDialog, setShowWorkProgressDialog] = useState(false);
  const [employeeType, setEmployeeType] = useState('field_based'); // office_based or field_based
  const [workProgressRequired, setWorkProgressRequired] = useState(true);
  
  // Help Center state
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  
  // Project Message Input
  const [projectMessageText, setProjectMessageText] = useState('');
  const [projectMessageImages, setProjectMessageImages] = useState([]);
  const [projectMessageVideo, setProjectMessageVideo] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [replyingToMessage, setReplyingToMessage] = useState(null); // Message being replied to
  const [selectedSubProject, setSelectedSubProject] = useState(null);
  const [subProjects, setSubProjects] = useState([]);
  const [mediaTags, setMediaTags] = useState('');
  const [mediaComments, setMediaComments] = useState({});
  
  // Image Viewer
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [showImageViewer, setShowImageViewer] = useState(false);
  
  const [expenses, setExpenses] = useState([]);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    expense_type: 'parking',
    amount: '',
    description: '',
    pay_period_start: '',
    pay_period_end: ''
  });
  const [expenseReceipts, setExpenseReceipts] = useState([]);
  const [uploadingReceipts, setUploadingReceipts] = useState(false);
  
  // Change Password state
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  
  // Company Settings
  const [companySettings, setCompanySettings] = useState({ 
    name: '', 
    tagline: '',
    logo_url: '',
    name_color: '#1e293b',
    name_font: 'Inter'
  });
  
  // Material Order
  const [showMaterialOrderDialog, setShowMaterialOrderDialog] = useState(false);
  const [showOpenOrdersDialog, setShowOpenOrdersDialog] = useState(false);

  // My Projects
  const [showProjectDetailsDialog, setShowProjectDetailsDialog] = useState(false);

  // Material Returns
  const [materialReturns, setMaterialReturns] = useState([]);
  
  // My Profile State
  const [showMyProfileDialog, setShowMyProfileDialog] = useState(false);
  // Bi-Weekly Hours breakdown (opens when "Hours Today" card is tapped)
  const [showBiweeklyDialog, setShowBiweeklyDialog] = useState(false);
  const [biweeklyData, setBiweeklyData] = useState(null);
  const [biweeklyLoading, setBiweeklyLoading] = useState(false);
  const [showPersonalDetailsDialog, setShowPersonalDetailsDialog] = useState(false);
  const [myProfile, setMyProfile] = useState(null);
  const [showAddCertificateDialog, setShowAddCertificateDialog] = useState(false);
  const [showAddLicenseDialog, setShowAddLicenseDialog] = useState(false);
  const [showAddCredentialDialog, setShowAddCredentialDialog] = useState(false);
  const [newCertificate, setNewCertificate] = useState({ name: '', issuing_authority: '', issue_date: '', expiry_date: '', certificate_number: '' });
  const [newLicense, setNewLicense] = useState({ license_type: '', license_number: '', state: '', issue_date: '', expiry_date: '' });
  const [newCredential, setNewCredential] = useState({ credential_type: '', credential_number: '', issue_date: '', expiry_date: '', notes: '' });
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnForm, setReturnForm] = useState({
    project_id: '',
    reason: '',
    notes: '',
    materials_description: ''
  });

  // Material Transfers
  const [materialTransfers, setMaterialTransfers] = useState([]);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferForm, setTransferForm] = useState({
    source_project_id: '',
    destination_project_id: '',
    reason: '',
    materials_description: ''
  });

  // Elapsed time for running clock display
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  
  // Helper function to format dates without timezone shift
  const formatDateDisplay = (dateString, options = {}) => {
    if (!dateString) return '';
    // Parse the date string directly without timezone conversion
    const parts = dateString.split('T')[0].split('-');
    if (parts.length !== 3) return dateString;
    const [year, month, day] = parts;
    
    // If options include weekday, format as "Wednesday, Apr 8, 2026"
    if (options.weekday) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      // Create date at noon UTC to avoid timezone shift
      const date = new Date(`${year}-${month}-${day}T12:00:00Z`);
      const weekday = weekdays[date.getUTCDay()];
      const monthName = months[parseInt(month) - 1];
      return `${weekday}, ${monthName} ${parseInt(day)}, ${year}`;
    }
    
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };
  
  // Calculate distance between two coordinates (in meters)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  // Play alert sound
  const playAlertSound = (type = 'warning') => {
    try {
      // Create audio context for sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      if (type === 'warning') {
        // Warning beep - two short beeps
        oscillator.frequency.value = 800;
        gainNode.gain.value = 0.3;
        oscillator.start();
        setTimeout(() => oscillator.stop(), 200);
      } else if (type === 'clockout') {
        // Clock-out alert - longer descending tone
        oscillator.frequency.value = 600;
        gainNode.gain.value = 0.4;
        oscillator.start();
        oscillator.frequency.linearRampToValueAtTime(300, audioContext.currentTime + 0.5);
        setTimeout(() => oscillator.stop(), 500);
      }
    } catch (e) {
      console.log('Audio not supported:', e);
    }
  };

  // Trigger vibration
  const triggerVibration = (pattern) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  };

  // Auto clock-out when leaving project proximity (150m)
  const handleAutoClockOut = async (distance) => {
    const token = localStorage.getItem('token');
    
    // Alert user with sound and vibration BEFORE the API call
    playAlertSound('clockout');
    triggerVibration([300, 100, 300, 100, 500]); // Strong vibration pattern
    
    try {
      const response = await fetch(`${API_URL}/api/time/clock-out`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          latitude: currentLocation?.latitude,
          longitude: currentLocation?.longitude,
          address: currentLocation?.address || 'Left project area',
          work_description: `Auto clock-out: Left project proximity (${Math.round(distance)}m away)`,
          is_auto_clockout: true
        })
      });

      if (response.ok) {
        toast.warning(`You've been automatically clocked out - you left the project area (${Math.round(distance)}m away from site)`, {
          duration: 8000 // Show for 8 seconds
        });
        setIsClockedIn(false);
        setClockInTime(null);
        setActiveTimeEntry(null);
        setProximityWarning(false);
        fetchData();
      }
    } catch (error) {
      console.error('Auto clock-out error:', error);
    }
  };

  // Warn user when approaching boundary
  const triggerProximityWarning = (distance) => {
    playAlertSound('warning');
    triggerVibration([200, 100, 200]); // Short double vibration
    toast.warning(`Warning: You are ${Math.round(distance)}m from the project. Auto clock-out at 150m.`, {
      duration: 5000
    });
    setProximityWarning(true);
  };

  // Watch location while clocked in for proximity-based auto clock-out
  useEffect(() => {
    if (isClockedIn && activeTimeEntry?.project_id && 'geolocation' in navigator) {
      // Get the project's location
      const project = projects.find(p => p.id === activeTimeEntry.project_id);
      
      if (project?.latitude && project?.longitude) {
        const watchId = navigator.geolocation.watchPosition(
          (position) => {
            const distance = calculateDistance(
              position.coords.latitude,
              position.coords.longitude,
              project.latitude,
              project.longitude
            );

            // Update current location with reverse geocoding
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16&addressdetails=1`)
              .then(res => res.json())
              .then(data => {
                const parts = [];
                if (data.address) {
                  if (data.address.road) parts.push(data.address.road);
                  if (data.address.suburb || data.address.neighbourhood) parts.push(data.address.suburb || data.address.neighbourhood);
                  if (data.address.city || data.address.town || data.address.village) parts.push(data.address.city || data.address.town || data.address.village);
                }
                const readableAddress = parts.length > 0 ? parts.join(', ') : `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
                setCurrentLocation({ latitude: lat, longitude: lng, address: readableAddress });
              })
              .catch(() => {
                setCurrentLocation({ latitude: lat, longitude: lng, address: `${lat.toFixed(4)}, ${lng.toFixed(4)}` });
              });

            // Warning at 120m (before auto clock-out threshold)
            if (distance > 120 && distance <= GEOFENCE_RADIUS) {
              if (!proximityWarning) {
                triggerProximityWarning(distance);
              }
            }
            // Auto clock-out at 150m+
            else if (distance > GEOFENCE_RADIUS) {
              handleAutoClockOut(distance);
            }
            // Reset warning if back within safe zone
            else if (distance <= 120) {
              if (proximityWarning) {
                setProximityWarning(false);
                toast.success('You are back within the project area');
              }
            }
          },
          (error) => {
            console.error('Geolocation watch error:', error);
          },
          {
            enableHighAccuracy: true,
            maximumAge: 10000, // 10 seconds
            timeout: 15000
          }
        );

        setLocationWatchId(watchId);
      }
    }

    // Cleanup: stop watching when clocked out or component unmounts
    return () => {
      if (locationWatchId) {
        navigator.geolocation.clearWatch(locationWatchId);
        setLocationWatchId(null);
      }
    };
  }, [isClockedIn, activeTimeEntry?.project_id, projects]);
  
  // Update elapsed time every second when clocked in
  useEffect(() => {
    let interval;
    if (isClockedIn && clockInTime) {
      const updateElapsed = () => {
        const now = new Date();
        const clockIn = new Date(clockInTime);
        const diff = Math.floor((now - clockIn) / 1000);
        
        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;
        
        setElapsedTime(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      };
      
      updateElapsed();
      interval = setInterval(updateElapsed, 1000);
    } else {
      setElapsedTime('00:00:00');
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isClockedIn, clockInTime]);
  
  useEffect(() => {
    fetchData();
    fetchExpenses();
    fetchReturns();
    fetchTransfers();
    fetchCompanySettings();
    fetchMyProfile();
    
    // Check location permission and get location
    const checkLocationPermission = async () => {
      if (!hasFeature(user, 'gps_tracking')) {
        setLocationPermission('granted');
        getCurrentLocation();
        return;
      }
      
      if (!('geolocation' in navigator)) {
        setLocationPermission('unavailable');
        return;
      }
      
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const result = await navigator.permissions.query({ name: 'geolocation' });
          setLocationPermission(result.state);
          
          if (result.state === 'granted') {
            getCurrentLocation();
          } else if (result.state === 'prompt') {
            // Trigger the browser permission dialog
            getCurrentLocation();
          }
          
          // Listen for permission changes
          result.onchange = () => {
            setLocationPermission(result.state);
            if (result.state === 'granted') {
              getCurrentLocation();
            }
          };
        } catch (e) {
          // Permissions API not supported, try getting location directly
          getCurrentLocation();
        }
      } else {
        // No Permissions API, try getting location directly
        getCurrentLocation();
      }
    };
    checkLocationPermission();
  }, [user?.role]);

  const fetchMyProfile = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/my-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const profile = await response.json();
        setMyProfile(profile);
        setIsFirstTimeUser(profile.is_first_time_user);
        
        // Check for expired certificates
        const expiredCerts = (profile.certificates || []).filter(cert => {
          if (cert.expiry_date) {
            return new Date(cert.expiry_date) < new Date();
          }
          return false;
        });
        
        // Check for missing mandatory certificates
        const mandatoryCertNames = ['WHMIS', 'Working at Heights', 'Fall Protection', 'Elevated Platform', 'Worker Health & Safety Awareness'];
        const uploadedCertNames = (profile.certificates || []).map(c => c.name.toLowerCase());
        const missingMandatory = mandatoryCertNames.filter(certName => 
          !uploadedCertNames.some(uploaded => uploaded.includes(certName.toLowerCase().split(' ')[0]))
        );
        
        // Show profile dialog for first-time users (only if not previously dismissed)
        const profilePromptDismissed = user?.id ? localStorage.getItem(`profile_prompt_dismissed_${user.id}`) : null;
        if (profile.is_first_time_user && !profilePromptDismissed && user?.id) {
          setTimeout(() => {
            toast.info('Welcome! Please complete your profile by adding your credentials, certificates, and licenses.');
            setShowMyProfileDialog(true);
            // Mark as dismissed so it won't show again
            localStorage.setItem(`profile_prompt_dismissed_${user.id}`, 'true');
          }, 2000);
        } 
        // Warn about expired certificates on every login
        else if (expiredCerts.length > 0) {
          setTimeout(() => {
            toast.error(`⚠️ You have ${expiredCerts.length} EXPIRED certificate(s)! Please update immediately.`, {
              duration: 8000
            });
            setShowMyProfileDialog(true);
          }, 2000);
          
          // Send notification to manager about expired certificates
          notifyManagerAboutExpiredCerts(expiredCerts);
        }
        // Warn about missing mandatory certificates
        else if (missingMandatory.length > 0) {
          setTimeout(() => {
            toast.warning(`You are missing ${missingMandatory.length} mandatory certificate(s). Please upload them.`, {
              duration: 6000
            });
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };
  
  // Function to notify manager about expired certificates
  const notifyManagerAboutExpiredCerts = async (expiredCerts) => {
    const token = localStorage.getItem('token');
    try {
      const certNames = expiredCerts.map(c => c.name).join(', ');
      const message = `⚠️ CERTIFICATE EXPIRY ALERT: ${user.name} has ${expiredCerts.length} expired certificate(s): ${certNames}. Please follow up to ensure compliance.`;
      
      await fetch(`${API_URL}/api/messages/certificate-alert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: message,
          message_type: 'certificate_alert'
        })
      });
      console.log('Manager notified about expired certificates');
    } catch (error) {
      console.error('Failed to notify manager:', error);
    }
  };

  const addMyCertificate = async () => {
    if (!newCertificate.name) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/my-profile/certificates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCertificate)
      });
      if (response.ok) {
        toast.success('Certificate added');
        setShowAddCertificateDialog(false);
        setNewCertificate({ name: '', issuing_authority: '', issue_date: '', expiry_date: '', certificate_number: '' });
        fetchMyProfile();
      } else {
        toast.error('Failed to add certificate');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const deleteMyCertificate = async (certId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/my-profile/certificates/${certId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Certificate deleted');
        fetchMyProfile();
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const uploadCertificateDocument = async (certId, file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/my-profile/certificates/${certId}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success('Document uploaded successfully');
        fetchMyProfile();
        return result.document_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload document');
      }
    } catch (error) {
      toast.error('Connection error');
    }
    return null;
  };

  const uploadLicenseDocument = async (licenseId, file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/my-profile/licenses/${licenseId}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('License document uploaded');
        fetchMyProfile();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const uploadCredentialDocument = async (credentialId, file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/my-profile/credentials/${credentialId}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('Credential document uploaded');
        fetchMyProfile();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const addMyLicense = async () => {
    if (!newLicense.license_type || !newLicense.license_number) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/my-profile/licenses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newLicense)
      });
      if (response.ok) {
        toast.success('License added');
        setShowAddLicenseDialog(false);
        setNewLicense({ license_type: '', license_number: '', state: '', issue_date: '', expiry_date: '' });
        fetchMyProfile();
      } else {
        toast.error('Failed to add license');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const deleteMyLicense = async (licenseId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/my-profile/licenses/${licenseId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('License deleted');
        fetchMyProfile();
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const addMyCredential = async () => {
    if (!newCredential.credential_type) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/my-profile/credentials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCredential)
      });
      if (response.ok) {
        toast.success('Credential added');
        setShowAddCredentialDialog(false);
        setNewCredential({ credential_type: '', credential_number: '', issue_date: '', expiry_date: '', notes: '' });
        fetchMyProfile();
      } else {
        toast.error('Failed to add credential');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const deleteMyCredential = async (credId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/my-profile/credentials/${credId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Credential deleted');
        fetchMyProfile();
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const fetchCompanySettings = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/company/branding`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const settings = await response.json();
        
        // Use logo_base64 if available (persistent), otherwise use logo_url
        let logoUrl = settings.logo_base64 || settings.logo_url || '';
        if (logoUrl && !logoUrl.startsWith('data:') && (logoUrl.startsWith('/uploads/') || logoUrl.startsWith('/api/uploads/'))) {
          logoUrl = `${API_URL}${logoUrl}`;
        }
        
        setCompanySettings({
          name: settings.name || '',
          tagline: settings.tagline || '',
          logo_url: logoUrl,
          name_color: settings.name_color || '#1e293b',
          name_font: settings.name_font || 'Inter'
        });
      }
    } catch (error) {
      console.error('Error fetching company branding:', error);
    }
  };

  const getCurrentLocation = () => {
    // Check if GPS tracking is available for this plan
    if (!hasFeature(user, 'gps_tracking')) {
      setCurrentLocation({
        latitude: 0,
        longitude: 0,
        address: 'GPS tracking not available (Starter plan)'
      });
      setGettingLocation(false);
      setLocationPermission('granted');
      return;
    }
    
    setGettingLocation(true);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          // Try reverse geocoding for a readable address
          fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16&addressdetails=1`)
            .then(res => res.json())
            .then(data => {
              const parts = [];
              if (data.address) {
                if (data.address.road) parts.push(data.address.road);
                if (data.address.suburb || data.address.neighbourhood) parts.push(data.address.suburb || data.address.neighbourhood);
                if (data.address.city || data.address.town || data.address.village) parts.push(data.address.city || data.address.town || data.address.village);
              }
              const readableAddress = parts.length > 0 ? parts.join(', ') : data.display_name?.split(',').slice(0, 3).join(',') || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
              setCurrentLocation({
                latitude: lat,
                longitude: lng,
                address: readableAddress
              });
            })
            .catch(() => {
              setCurrentLocation({
                latitude: lat,
                longitude: lng,
                address: `${lat.toFixed(4)}, ${lng.toFixed(4)}`
              });
            });
          
          setGettingLocation(false);
          setLocationPermission('granted');
        },
        (error) => {
          console.error('Error getting location:', error);
          if (error.code === 1) {
            // PERMISSION_DENIED
            setLocationPermission('denied');
          } else {
            setLocationPermission('error');
          }
          setCurrentLocation(null);
          setGettingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    } else {
      setLocationPermission('unavailable');
      setCurrentLocation(null);
      setGettingLocation(false);
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    try {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const today = `${yyyy}-${mm}-${dd}`;
      
      // Calculate current week Mon-Sat for schedule display
      const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon...6=Sat
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(yyyy, now.getMonth(), now.getDate() + mondayOffset);
      const saturday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 5);
      const weekStart = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
      const weekEnd = `${saturday.getFullYear()}-${String(saturday.getMonth() + 1).padStart(2, '0')}-${String(saturday.getDate()).padStart(2, '0')}`;
      
      const [schedRes, tasksRes, timeRes, msgRes, ordersRes, projRes, profileRes] = await Promise.all([
        fetch(`${API_URL}/api/schedules?start_date=${weekStart}&end_date=${weekEnd}`, { 
          headers: { 'Authorization': `Bearer ${token}` } 
        }),
        fetch(`${API_URL}/api/tasks`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/time/entries`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/messages`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/material-orders`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/projects`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/my-profile`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      // Set employee type from profile (for work progress dialog logic)
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        if (profileData.employee_type) {
          setEmployeeType(profileData.employee_type);
          // Set work progress required based on employee type
          setWorkProgressRequired(profileData.employee_type !== 'office_based');
        }
      }

      if (schedRes.ok) {
        const schedData = await schedRes.json();
        setSchedules(schedData);
        
        // Find all today's schedules (could be multiple projects)
        const todaySchedules = schedData.filter(s => s.date === today);
        
        // Check if today's shift has ended — if so, show tomorrow's schedule
        const now = new Date();
        let shiftEnded = false;
        if (todaySchedules.length > 0 && todaySchedules[0].end_time) {
          const [endH, endM] = todaySchedules[0].end_time.split(':').map(Number);
          const shiftEnd = new Date(now);
          shiftEnd.setHours(endH, endM, 0, 0);
          if (now > shiftEnd) {
            shiftEnded = true;
          }
        }

        // Calculate tomorrow's date string
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth()+1).padStart(2,'0')}-${String(tomorrow.getDate()).padStart(2,'0')}`;
        const tomorrowSchedules = schedData.filter(s => s.date === tomorrowStr);

        // Use tomorrow's schedule if today's shift ended and tomorrow has one
        const displaySchedules = (shiftEnded && tomorrowSchedules.length > 0) ? tomorrowSchedules : todaySchedules;
        
        if (displaySchedules.length > 0) {
          setTodaySchedule(displaySchedules[0]); // For banner display
          
          // Get unique projects for display
          const uniqueProjects = [];
          const projectIds = new Set();
          
          displaySchedules.forEach(sched => {
            if (!projectIds.has(sched.project_id)) {
              projectIds.add(sched.project_id);
              uniqueProjects.push({
                id: sched.project_id,
                name: sched.project_name,
                address: sched.project_address
              });
            }
          });
          
          setTodayProjects(uniqueProjects);
          
          // Auto-select if only one project
          if (uniqueProjects.length === 1) {
            setSelectedProject(uniqueProjects[0].id);
          }
        } else {
          setTodaySchedule(null);
          setTodayProjects([]);
          setSelectedProject(null);
        }
      }
      
      if (tasksRes.ok) setTasks(await tasksRes.json());
      
      if (timeRes.ok) {
        const entries = await timeRes.json();
        setTimeEntries(entries);
        
        // Check if currently clocked in
        const activeEntry = entries.find(entry => !entry.clock_out);
        setIsClockedIn(!!activeEntry);
        setActiveTimeEntry(activeEntry);
        // Set clock in time for running timer
        if (activeEntry) {
          setClockInTime(new Date(activeEntry.clock_in));
        } else {
          setClockInTime(null);
        }
        
        // Fetch today's hours from backend (uses company timezone)
        try {
          const summaryRes = await fetch(`${API_URL}/api/time/summary`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (summaryRes.ok) {
            const summary = await summaryRes.json();
            // Add current running time if clocked in
            let todayTotal = summary.today_hours || 0;
            if (activeEntry) {
              const clockIn = new Date(activeEntry.clock_in);
              const now = new Date();
              todayTotal += (now - clockIn) / (1000 * 60 * 60);
            }
            setHoursToday(todayTotal.toFixed(2));
          }
        } catch (err) {
          console.error('Error fetching time summary:', err);
        }
      }
      
      if (msgRes.ok) setMessages(await msgRes.json());
      if (ordersRes.ok) setMaterialOrders(await ordersRes.json());
      if (projRes.ok) setProjects(await projRes.json());

      // Fetch all active projects for transfers (using include_all_active parameter)
      const allProjRes = await fetch(`${API_URL}/api/projects?include_all_active=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (allProjRes.ok) {
        const allProjs = await allProjRes.json();
        console.log('All active projects for transfer:', allProjs.length, allProjs.map(p => p.name));
        setAllProjects(allProjs);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchReturns = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/material-returns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMaterialReturns(data);
      }
    } catch (error) {
      console.error('Error fetching returns:', error);
    }
  };

  const fetchTransfers = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/material-transfers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMaterialTransfers(data);
      }
    } catch (error) {
      console.error('Error fetching transfers:', error);
    }
  };

  const handleClockIn = async () => {
    if (locationPermission === 'denied' || locationPermission === 'unavailable') {
      toast.error('Location services are required. Please enable location in your browser/phone settings and refresh.');
      return;
    }
    
    if (!currentLocation || (currentLocation.latitude === 0 && currentLocation.longitude === 0)) {
      toast.error('Getting your location... Please wait and try again.');
      getCurrentLocation();
      return;
    }

    // Check if multiple projects - need selection
    if (todayProjects.length > 1 && !selectedProject) {
      setShowProjectSelect(true);
      toast.error('Please select a project to clock in');
      return;
    }

    // Check if no projects assigned today
    if (todayProjects.length === 0) {
      toast.error('You are not assigned to any project today');
      return;
    }

    // Check if shift has ended + 1 hour grace period
    if (todaySchedule && todaySchedule.end_time) {
      const now = new Date();
      const [endH, endM] = todaySchedule.end_time.split(':').map(Number);
      const shiftEnd = new Date();
      shiftEnd.setHours(endH, endM, 0, 0);
      // Add 1 hour grace period
      const cutoff = new Date(shiftEnd.getTime() + 60 * 60 * 1000);
      if (now > cutoff) {
        toast.error(`Clock-in not allowed. Your shift ended at ${todaySchedule.end_time}`);
        return;
      }
    }

    // Check if too early before shift starts (30 min before)
    if (todaySchedule && todaySchedule.start_time) {
      const now = new Date();
      const [startH, startM] = todaySchedule.start_time.split(':').map(Number);
      const shiftStart = new Date();
      shiftStart.setHours(startH, startM, 0, 0);
      const earlyLimit = new Date(shiftStart.getTime() - 30 * 60 * 1000);
      if (now < earlyLimit) {
        toast.error(`Clock-in not allowed. Your shift starts at ${todaySchedule.start_time}. You can clock in 30 minutes before.`);
        return;
      }
    }

    const projectId = selectedProject || todayProjects[0]?.id;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/time/clock-in`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          project_id: projectId,
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude,
          address: currentLocation.address
        })
      });

      if (response.ok) {
        const data = await response.json();
        const projectName = todayProjects.find(p => p.id === projectId)?.name || 'project';
        toast.success(`Clocked in to ${projectName} at ${currentLocation.address}`);
        setIsClockedIn(true);
        setActiveTimeEntry(data); // Set active time entry immediately for UI update
        setClockInTime(new Date(data.clock_in || new Date())); // Set clock in time for running timer
        setShowProjectSelect(false);
        // Set employee type from response
        if (data.employee_type) {
          setEmployeeType(data.employee_type);
        }
        if (data.work_progress_required !== undefined) {
          setWorkProgressRequired(data.work_progress_required);
        }
        fetchData(); // Refresh all data in background
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock in');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleClockOut = async () => {
    if (!currentLocation) {
      toast.error('Getting location... Please wait and try again.');
      getCurrentLocation();
      return;
    }

    // For office-based employees, clock out directly without work progress dialog
    if (employeeType === 'office_based' || !workProgressRequired) {
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(`${API_URL}/api/time/clock-out`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            latitude: currentLocation.latitude,
            longitude: currentLocation.longitude,
            address: currentLocation.address
          })
        });

        if (response.ok) {
          const data = await response.json();
          toast.success(`Clocked out successfully! Hours worked: ${data.hours_worked}`);
          setIsClockedIn(false);
          setClockInTime(null); // Reset clock in time
          setActiveTimeEntry(null); // Clear active time entry immediately
          fetchData();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to clock out');
        }
      } catch (error) {
        toast.error('Connection error');
      }
    } else {
      // Show work progress dialog for field-based employees
      setShowWorkProgressDialog(true);
    }
  };

  const handleProjectImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingMedia(true);
    const token = localStorage.getItem('token');
    
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const response = await fetch(`${API_URL}/api/upload-images`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        // Convert /uploads/filename.jpg to /api/image/filename.jpg
        const apiUrls = data.image_urls.map(url => {
          const filename = url.split('/').pop();
          return `${API_URL}/api/image/${filename}`;
        });
        setProjectMessageImages([...projectMessageImages, ...apiUrls]);
        toast.success('Images uploaded successfully');
      } else {
        toast.error('Failed to upload images');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload error');
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleProjectVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingMedia(true);
    const token = localStorage.getItem('token');
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/upload-video`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        // Convert /uploads/filename.mp4 to /api/image/filename.mp4
        const filename = data.video_url.split('/').pop();
        const apiUrl = `${API_URL}/api/image/${filename}`;
        setProjectMessageVideo(apiUrl);
        toast.success('Video uploaded successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload video');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload error');
    } finally {
      setUploadingMedia(false);
    }
  };

  const fetchSubProjects = async (projectId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/sub-projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubProjects(data);
      }
    } catch (error) {
      console.error('Error fetching sub-projects:', error);
    }
  };

  const handleSendProjectMessage = async (e) => {
    e.preventDefault();
    if (!projectMessageText.trim() || !selectedProject) return;

    const token = localStorage.getItem('token');
    try {
      // Parse media tags
      const tagsArray = mediaTags.split(',').map(tag => tag.trim()).filter(tag => tag);
      
      const response = await fetch(`${API_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: projectMessageText,
          message_type: 'project',
          project_id: selectedProject.id,
          sub_project_id: selectedSubProject?.id || null,
          reply_to_id: replyingToMessage?.id || null,
          image_urls: projectMessageImages,
          video_url: projectMessageVideo,
          media_tags: tagsArray,
          media_comments: mediaComments
        })
      });

      if (response.ok) {
        toast.success('Message sent successfully');
        setProjectMessageText('');
        setProjectMessageImages([]);
        setProjectMessageVideo(null);
        setReplyingToMessage(null);
        setMediaTags('');
        setMediaComments({});
        fetchData(); // Refresh messages
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleClockOutSuccess = () => {
    setIsClockedIn(false);
    setClockInTime(null); // Reset clock in time
    setShowWorkProgressDialog(false);
    fetchData();
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Task updated');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: messageText })
      });

      if (response.ok) {
        setMessageText('');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const fetchExpenses = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/expenses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setExpenses(await response.json());
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const handleSubmitExpense = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setUploadingReceipts(true);
    
    try {
      let receiptUrls = [];
      
      // Upload receipts if any
      if (expenseReceipts.length > 0) {
        const formData = new FormData();
        expenseReceipts.forEach(file => formData.append('files', file));
        
        const uploadResponse = await fetch(`${API_URL}/api/upload-images`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (uploadResponse.ok) {
          const result = await uploadResponse.json();
          receiptUrls = result.image_urls;
        } else {
          toast.error('Failed to upload receipts');
          setUploadingReceipts(false);
          return;
        }
      }
      
      // Submit expense
      const response = await fetch(`${API_URL}/api/expenses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...expenseForm,
          amount: parseFloat(expenseForm.amount),
          receipt_urls: receiptUrls
        })
      });

      if (response.ok) {
        toast.success('Expense submitted successfully');
        setShowExpenseDialog(false);
        setExpenseForm({
          expense_type: 'parking',
          amount: '',
          description: '',
          pay_period_start: '',
          pay_period_end: ''
        });
        setExpenseReceipts([]);
        fetchExpenses();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit expense');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setUploadingReceipts(false);
    }
  };

  const handleSubmitMaterialOrder = async (orderData) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/material-orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        toast.success('Material order created successfully');
        setShowMaterialOrderDialog(false);
        fetchData(); // Refresh orders
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create order');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const handleSubmitReturn = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_URL}/api/material-returns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(returnForm)
      });

      if (response.ok) {
        toast.success('Material return request submitted');
        setShowReturnDialog(false);
        setReturnForm({
          project_id: '',
          reason: '',
          notes: '',
          materials_description: ''
        });
        fetchReturns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit return');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleSubmitTransfer = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_URL}/api/material-transfers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transferForm)
      });

      if (response.ok) {
        toast.success('Material transfer request submitted');
        setShowTransferDialog(false);
        setTransferForm({
          source_project_id: '',
          destination_project_id: '',
          reason: '',
          materials_description: ''
        });
        fetchTransfers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit transfer');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  return (
    <div className="employee-dashboard modern-bg" data-testid="employee-dashboard">
      {/* Location Permission Blocker */}
      {hasFeature(user, 'gps_tracking') && locationPermission === 'denied' && (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" data-testid="location-blocker">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPin className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Location Required</h2>
              <p className="text-sm text-gray-600 mt-2">Location services must be enabled to use this app. This is required for attendance tracking.</p>
            </div>
            <div className="space-y-3 mb-5">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-sm font-semibold text-blue-800 mb-1">iPhone / Safari:</p>
                <p className="text-xs text-blue-700">Settings &gt; Safari &gt; Location &gt; Allow</p>
                <p className="text-xs text-blue-700">Also check: Settings &gt; Privacy &gt; Location Services &gt; ON</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-sm font-semibold text-green-800 mb-1">Android / Chrome:</p>
                <p className="text-xs text-green-700">Tap the lock icon in the address bar &gt; Permissions &gt; Location &gt; Allow</p>
                <p className="text-xs text-green-700">Also check: Phone Settings &gt; Location &gt; ON</p>
              </div>
            </div>
            <Button 
              className="w-full" 
              onClick={() => {
                setLocationPermission('checking');
                getCurrentLocation();
              }}
              data-testid="retry-location-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              I've Enabled Location - Retry
            </Button>
            <Button 
              variant="outline"
              className="w-full mt-2"
              onClick={() => setLocationPermission('dismissed')}
              data-testid="continue-without-location-btn"
            >
              Continue Without Location
            </Button>
            <Button 
              variant="ghost"
              className="w-full mt-1 text-gray-500"
              onClick={() => {
                localStorage.removeItem('token');
                setUser(null);
                navigate('/auth');
              }}
              data-testid="blocker-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
            <p className="text-xs text-center text-gray-400 mt-2">Clock-in requires location enabled</p>
          </div>
        </div>
      )}

      {/* Company Branding Header */}
      <div className="company-header">
        <div className="company-branding">
          <div className="company-logo-circle">
            {companySettings.logo_url ? (
              <img src={companySettings.logo_url} alt="Company logo" className="company-logo-img" />
            ) : (
              <span className="company-initials">
                {companySettings.name ? companySettings.name.substring(0, 2).toUpperCase() : 'MI'}
              </span>
            )}
          </div>
          <div className="company-info">
            <h2 
              className="company-name"
              style={{ 
                color: companySettings.name_color || '#1e293b',
                fontFamily: companySettings.name_font || 'Inter'
              }}
            >
              {companySettings.name || 'Mechanical Insulation Co.'}
            </h2>
            <p className="company-tagline">
              {companySettings.tagline || 'Employee Portal'}
            </p>
          </div>
        </div>
        <Button onClick={handleLogout} variant="outline" className="logout-btn" data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>

      {/* PWA Install Banner */}
      <PWAInstallBanner user={user} />

      {/* iOS Install Instructions Banner */}
      <IOSInstallBanner />

      {/* Personalized Dashboard Header */}
      <header className="dashboard-header-modern">
        <div style={{ width: '100%' }}>
          <h1 className="personalized-title" data-testid="dashboard-title">
            {user.name}'s Dashboard
          </h1>
          <p className="welcome-text">Welcome back! Here's what's happening today.</p>
          <div className="flex items-center flex-wrap gap-2 mt-2">
            <Button onClick={() => setShowChangePasswordDialog(true)} variant="outline" className="action-btn-small" style={{ background: 'white' }}>
              🔒 Change Password
            </Button>
            <Button onClick={() => setShowMyProfileDialog(true)} variant="outline" className="action-btn-small" style={{ background: 'white' }}>
              <User className="w-4 h-4 mr-1" />
              My Profile
              {isFirstTimeUser && <span className="ml-1 text-red-500">!</span>}
            </Button>
            <NotificationCenter />
            <PushNotificationManager authToken={localStorage.getItem('token')} compact user={user} />
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setShowHelpCenter(true)}
              className="text-gray-600 hover:text-blue-600"
              data-testid="help-btn"
            >
              <HelpCircle className="w-6 h-6" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={async () => {
                toast.info('Updating app...');
                await forceAppUpdate();
              }}
              className="text-gray-600 hover:text-green-600"
              title="Update App"
              data-testid="update-app-btn"
            >
              <RefreshCw className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </header>

      {/* Schedule Banner */}
      {todaySchedule && (
        <Card className="mb-6 bg-blue-50 border-blue-200" data-testid="today-schedule-banner">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <Calendar className="w-5 h-5" />
              {(() => {
                const today = new Date();
                const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
                const isToday = todaySchedule.date === todayStr;
                const schedDate = todaySchedule.date ? todaySchedule.date.split('-') : null;
                const dateDisplay = schedDate ? `${schedDate[2]}/${schedDate[1]}/${schedDate[0]}` : '';
                return isToday ? `Today's Schedule   ${dateDisplay}` : `Tomorrow's Schedule   ${dateDisplay}`;
              })()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Project</p>
                <p className="font-semibold text-lg">{todaySchedule.project_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Location</p>
                <p className="font-medium">{todaySchedule.project_address || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Team Members</p>
                <p className="font-medium">
                  {todaySchedule.team_details?.map(tm => tm.name).join(', ') || 'You'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats - 3 columns on all devices */}
      <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4 md:mb-6">
        {/* Hours Today — tap to open Bi-Weekly Hours breakdown */}
        <Card
          className="stat-card stat-card-green cursor-pointer hover:ring-2 hover:ring-green-300"
          data-testid="hours-today-card"
          onClick={async () => {
            setShowBiweeklyDialog(true);
            setBiweeklyLoading(true);
            try {
              const token = localStorage.getItem('token');
              const res = await fetch(`${API_URL}/api/time/personal-weekly-breakdown?weeks=2`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (res.ok) setBiweeklyData(await res.json());
            } catch {
              toast.error('Could not load hours');
            } finally {
              setBiweeklyLoading(false);
            }
          }}
        >
          <CardContent className="pt-3 md:pt-6 pb-3 md:pb-4">
            <div className="text-center">
              <Clock className="w-5 h-5 md:w-8 md:h-8 mx-auto mb-1 text-green-800" />
              <p className="text-xs md:text-sm font-semibold text-gray-700">Hours Today</p>
              <p className="text-xl md:text-3xl font-bold text-green-900">{hoursToday}</p>
              <p className="text-[10px] text-green-700 mt-0.5">Tap for bi-weekly</p>
            </div>
          </CardContent>
        </Card>

        {/* Clock In / Clock Out - Central action card */}
        <Card className={`stat-card ${isClockedIn ? 'bg-blue-600' : 'bg-gray-900'}`} data-testid="clock-action-card">
          <CardContent className="pt-3 md:pt-6 pb-3 md:pb-4">
            <div className="text-center">
              <Clock className={`w-5 h-5 md:w-8 md:h-8 mx-auto mb-1 ${isClockedIn ? 'text-blue-200' : 'text-gray-400'}`} />
              {isClockedIn ? (
                <>
                  <p className="text-xs md:text-sm font-semibold text-blue-100 mb-0.5">Clocked In</p>
                  <p className="text-lg md:text-xl font-mono font-bold text-white mb-1" data-testid="running-time">
                    {elapsedTime}
                  </p>
                  {/* Proximity tracking indicator */}
                  {locationWatchId && (
                    <div className="flex items-center justify-center gap-1 mb-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <p className="text-xs text-blue-200">GPS tracking active</p>
                    </div>
                  )}
                  {proximityWarning && (
                    <div className="bg-yellow-500/30 text-yellow-200 text-xs px-2 py-1 rounded mb-2">
                      ⚠️ You're near the boundary!
                    </div>
                  )}
                  <Button 
                    onClick={handleClockOut} 
                    variant="destructive" 
                    size="sm"
                    className="w-full text-xs md:text-sm h-8 md:h-9"
                    data-testid="clock-out-btn"
                  >
                    Clock Out
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-xs md:text-sm font-semibold text-gray-300 mb-1">Ready?</p>
                  <Button 
                    onClick={handleClockIn} 
                    className="w-full bg-green-600 hover:bg-green-700 text-xs md:text-sm h-8 md:h-9"
                    size="sm"
                    data-testid="clock-in-btn"
                  >
                    Clock In
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Your Location */}
        <Card className="stat-card stat-card-purple" data-testid="location-card">
          <CardContent className="pt-3 md:pt-6 pb-3 md:pb-4">
            <div className="text-center">
              <MapPin className="w-5 h-5 md:w-8 md:h-8 mx-auto mb-1 text-purple-800" />
              <p className="text-xs md:text-sm font-semibold text-gray-700">Your Location</p>
              {gettingLocation ? (
                <p className="text-xs text-purple-900">Getting...</p>
              ) : currentLocation ? (
                <p className="text-xs text-purple-900 font-mono truncate">{currentLocation.address}</p>
              ) : (
                <Button size="sm" variant="outline" onClick={getCurrentLocation} className="mt-1 text-xs h-6 md:h-8 px-2">
                  Enable
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons-container" style={{display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem'}}>
        <Button 
          onClick={() => setShowMaterialOrderDialog(true)}
          className="action-btn action-btn-primary"
          size="lg"
        >
          <Package className="w-5 h-5 mr-2" />
          Create Material Order
        </Button>
        <Button 
          onClick={() => setShowOpenOrdersDialog(true)}
          className="action-btn action-btn-secondary"
          size="lg"
          variant="outline"
        >
          <Package className="w-5 h-5 mr-2" />
          View Open Orders
        </Button>
        <Button 
          onClick={() => {
            if (projects.length > 0) {
              setSelectedProject(projects[0]);
              setShowProjectDetailsDialog(true);
            }
          }}
          className="action-btn action-btn-secondary"
          size="lg"
          variant="outline"
        >
          <Calendar className="w-5 h-5 mr-2" />
          My Projects
        </Button>
        <Button 
          onClick={() => setShowReturnDialog(true)}
          className="action-btn action-btn-secondary"
          size="lg"
          variant="outline"
        >
          <TrendingUp className="w-5 h-5 mr-2" />
          Request Return
        </Button>
        <Button 
          onClick={() => setShowTransferDialog(true)}
          className="action-btn action-btn-secondary"
          size="lg"
          variant="outline"
        >
          <TrendingUp className="w-5 h-5 mr-2" />
          Request Transfer
        </Button>
        <Button 
          onClick={() => setShowExpenseDialog(true)}
          className="action-btn action-btn-secondary"
          size="lg"
          variant="outline"
        >
          <TrendingUp className="w-5 h-5 mr-2" />
          Submit Expense
        </Button>
        <ProjectProgressReporter 
          projects={projects} 
          todaySchedule={todaySchedule} 
          currentLocation={currentLocation} 
        />
      </div>

      <div className="dashboard-grid">
        {/* Messages */}
        <Card data-testid="messages-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Team Messages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="messages-container" data-testid="messages-container">
              {messages.map((msg, index) => (
                <div key={msg.id} className="message" data-testid={`message-${index}`}>
                  <div className="message-header">
                    <strong>{msg.sender_name}</strong>
                    <span>{new Date(msg.timestamp).toLocaleString()}</span>
                  </div>
                  <div>{msg.message}</div>
                </div>
              ))}
            </div>
            <form onSubmit={handleSendMessage} className="message-form">
              <Input
                placeholder="Type a message..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                data-testid="message-input"
              />
              <Button type="submit" data-testid="send-message-btn">Send</Button>
            </form>
          </CardContent>
        </Card>

        {/* Tasks */}
        <MyTasksCard tasks={tasks} onRefresh={fetchData} />

        {/* Schedule */}
        <Card data-testid="schedule-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              My Schedule (This Week)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="schedules-list space-y-2">
              {(() => {
                const now = new Date();
                const dayOfWeek = now.getDay();
                const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset);
                const days = [];
                const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                for (let i = 0; i < 6; i++) {
                  const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
                  const yyyy = d.getFullYear();
                  const mm = String(d.getMonth() + 1).padStart(2, '0');
                  const dd = String(d.getDate()).padStart(2, '0');
                  days.push({ dateStr: `${yyyy}-${mm}-${dd}`, dayName: dayNames[d.getDay()] });
                }
                const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                
                return days.map(({ dateStr, dayName }) => {
                  const daySchedules = schedules.filter(s => s.date === dateStr);
                  const isToday = dateStr === todayStr;
                  return (
                    <div key={dateStr} className={`p-3 rounded-lg border ${isToday ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-sm font-semibold ${isToday ? 'text-blue-700' : 'text-gray-700'}`}>
                          {dayName}
                        </span>
                        <span className="text-xs text-gray-500">{formatDateDisplay(dateStr)}</span>
                        {isToday && <Badge variant="default" className="text-xs px-1.5 py-0">Today</Badge>}
                      </div>
                      {daySchedules.length > 0 ? (
                        daySchedules.map((schedule, sIdx) => (
                          <div key={sIdx} className="ml-2 mt-1 text-sm">
                            <span className="font-medium">{schedule.project_name}</span>
                            {schedule.start_time && schedule.end_time && (
                              <span className="text-gray-500 ml-2">{schedule.start_time} - {schedule.end_time}</span>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="ml-2 text-xs text-gray-400 italic">No schedule</p>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </CardContent>
        </Card>

        {/* Time Tracking - Shows location and recent entries only (Clock In/Out is in stats row above) */}
        <Card data-testid="time-tracking-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Time Tracking
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="time-tracking">
              {/* Current Status */}
              <div className="mb-4 p-3 rounded-lg bg-gray-50">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Status:</span>
                  <Badge variant={isClockedIn ? 'default' : 'secondary'}>
                    {isClockedIn ? 'Clocked In' : 'Clocked Out'}
                  </Badge>
                </div>
                {isClockedIn && activeTimeEntry?.clock_in_location && (
                  <p className="text-xs text-gray-600 mt-2">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    {activeTimeEntry.clock_in_location.address}
                  </p>
                )}
              </div>

              {/* Project Selection (if multiple projects today and not clocked in) */}
              {!isClockedIn && todayProjects.length > 1 && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Project to Clock In:
                  </label>
                  <select
                    value={selectedProject || ''}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">-- Select a project --</option>
                    {todayProjects.map(proj => (
                      <option key={proj.id} value={proj.id}>
                        {proj.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="time-entries mt-4">
                <h4>Recent Entries</h4>
                {timeEntries.slice(0, 5).map((entry, index) => (
                  <div key={entry.id} className="time-entry" data-testid={`time-entry-${index}`}>
                    <div>
                      <strong>Clock In:</strong> {new Date(entry.clock_in).toLocaleString()}
                    </div>
                    {entry.clock_out && (
                      <div>
                        <strong>Clock Out:</strong> {new Date(entry.clock_out).toLocaleString()}
                      </div>
                    )}
                    {entry.clock_in_location && (
                      <div className="text-xs text-gray-600">
                        <MapPin className="w-3 h-3 inline mr-1" />
                        {entry.clock_in_location.address}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Open Orders Card */}
      </div>

      {/* Open Orders Dialog */}
      <Dialog open={showOpenOrdersDialog} onOpenChange={setShowOpenOrdersDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Open Material Orders</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Created Orders */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Badge variant="warning">Created Orders</Badge>
                <span className="text-sm text-gray-500">
                  ({materialOrders.filter(o => o.status === 'pending' || o.status === 'created').length})
                </span>
              </h4>
              <div className="space-y-2">
                {materialOrders
                  .filter(order => order.status === 'pending' || order.status === 'created')
                  .map((order) => {
                    const project = projects.find(p => p.id === order.project_id);
                    return (
                      <div 
                        key={order.id} 
                        className="p-3 border rounded hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowOrderDetails(true);
                          setShowOpenOrdersDialog(false);
                        }}
                      >
                        <div className="flex justify-between">
                          <div>
                            <strong>{project?.name || 'Unknown Project'}</strong>
                            <p className="text-sm text-gray-600">
                              {new Date(order.order_date).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge variant="warning">{order.status}</Badge>
                        </div>
                      </div>
                    );
                  })}
                {materialOrders.filter(o => o.status === 'pending' || o.status === 'created').length === 0 && (
                  <p className="text-gray-500 text-sm italic">No created orders</p>
                )}
              </div>
            </div>

            {/* Ordered */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Badge variant="default">Ordered</Badge>
                <span className="text-sm text-gray-500">
                  ({materialOrders.filter(o => o.status === 'ordered').length})
                </span>
              </h4>
              <div className="space-y-2">
                {materialOrders
                  .filter(order => order.status === 'ordered')
                  .map((order) => {
                    const project = projects.find(p => p.id === order.project_id);
                    return (
                      <div 
                        key={order.id} 
                        className="p-3 border rounded hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowOrderDetails(true);
                          setShowOpenOrdersDialog(false);
                        }}
                      >
                        <div className="flex justify-between">
                          <div>
                            <strong>{project?.name || 'Unknown Project'}</strong>
                            <p className="text-sm text-gray-600">
                              {new Date(order.order_date).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge variant="default">{order.status}</Badge>
                        </div>
                      </div>
                    );
                  })}
                {materialOrders.filter(o => o.status === 'ordered').length === 0 && (
                  <p className="text-gray-500 text-sm italic">No ordered items</p>
                )}
              </div>
            </div>

            {/* Received at Site */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Badge variant="success">Received at Site</Badge>
                <span className="text-sm text-gray-500">
                  ({materialOrders.filter(o => o.status === 'received_at_site').length})
                </span>
              </h4>
              <div className="space-y-2">
                {materialOrders
                  .filter(order => order.status === 'received_at_site')
                  .map((order) => {
                    const project = projects.find(p => p.id === order.project_id);
                    return (
                      <div 
                        key={order.id} 
                        className="p-3 border rounded hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowOrderDetails(true);
                          setShowOpenOrdersDialog(false);
                        }}
                      >
                        <div className="flex justify-between">
                          <div>
                            <strong>{project?.name || 'Unknown Project'}</strong>
                            <p className="text-sm text-gray-600">
                              {new Date(order.order_date).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge variant="success">{order.status}</Badge>
                        </div>
                      </div>
                    );
                  })}
                {materialOrders.filter(o => o.status === 'received_at_site').length === 0 && (
                  <p className="text-gray-500 text-sm italic">No received items</p>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog open={showOrderDetails} onOpenChange={setShowOrderDetails}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Project</p>
                <p className="font-semibold">
                  {projects.find(p => p.id === selectedOrder.project_id)?.name || 'Unknown'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Order Date</p>
                  <p className="font-semibold">
                    {new Date(selectedOrder.order_date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Delivery Date</p>
                  <p className="font-semibold">
                    {selectedOrder.delivery_date ? new Date(selectedOrder.delivery_date).toLocaleDateString() : 'Not set'}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <Badge variant={
                  selectedOrder.status === 'received_at_site' ? 'success' :
                  selectedOrder.status === 'ordered' ? 'default' : 'warning'
                }>
                  {selectedOrder.status}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-600">Order Type</p>
                <p className="font-semibold">{selectedOrder.order_type}</p>
              </div>
              {selectedOrder.notes && (
                <div>
                  <p className="text-sm text-gray-600">Notes</p>
                  <p>{selectedOrder.notes}</p>
                </div>
              )}
              <div className="pt-4 border-t">
                <Button onClick={() => setShowOrderDetails(false)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Material Order Dialog */}
      <Dialog open={showMaterialOrderDialog} onOpenChange={setShowMaterialOrderDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Material Order</DialogTitle>
          </DialogHeader>
          <MaterialOrderForm
            projects={projects}
            onSubmit={handleSubmitMaterialOrder}
            onCancel={() => setShowMaterialOrderDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Expense Submission Dialog */}
      <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Submit Expense for Reimbursement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitExpense} className="space-y-4">
            <div>
              <Label>Expense Type *</Label>
              <Select 
                value={expenseForm.expense_type} 
                onValueChange={(val) => setExpenseForm({ ...expenseForm, expense_type: val })}
                required
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="parking">Parking</SelectItem>
                  <SelectItem value="materials">Materials</SelectItem>
                  <SelectItem value="tools">Tools</SelectItem>
                  <SelectItem value="fuel">Fuel</SelectItem>
                  <SelectItem value="meals">Meals</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Amount ($) *</Label>
              <Input
                type="number"
                step="0.01"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                placeholder="0.00"
                required
              />
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                placeholder="Describe the expense..."
                rows={3}
                required
              />
            </div>

            <div>
              <Label>Upload Receipts/Invoices</Label>
              <Input
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={(e) => setExpenseReceipts(Array.from(e.target.files))}
                className="cursor-pointer"
              />
              <p className="text-xs text-gray-500 mt-1">
                Upload photos of receipts or invoice PDFs (optional but recommended)
              </p>
              {expenseReceipts.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium">Selected files:</p>
                  <ul className="text-sm text-gray-600">
                    {expenseReceipts.map((file, i) => (
                      <li key={i}>• {file.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="p-3 bg-blue-50 rounded border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This expense will be automatically linked to the current bi-weekly pay period.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowExpenseDialog(false);
                  setExpenseForm({ expense_type: 'parking', amount: '', description: '' });
                  setExpenseReceipts([]);
                }}
                disabled={uploadingReceipts}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={uploadingReceipts}>
                {uploadingReceipts ? 'Submitting...' : 'Submit Expense'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Material Return Dialog */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Material Return</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="return_project">Project *</Label>
              <ProjectCombobox
                projects={projects}
                value={returnForm.project_id}
                onChange={(value) => setReturnForm({ ...returnForm, project_id: value })}
                placeholder="Select project"
                filterActive={false}
                testId="return-project"
              />
            </div>

            <div>
              <Label htmlFor="return_reason">Reason for Return *</Label>
              <Select
                value={returnForm.reason}
                onValueChange={(value) => setReturnForm({ ...returnForm, reason: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wrong_material_delivered">Wrong Material Delivered</SelectItem>
                  <SelectItem value="wrong_material_ordered">Wrong Material Ordered</SelectItem>
                  <SelectItem value="remaining_after_completion">Remaining After Project Completion</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="return_notes">Additional Notes</Label>
              <Textarea
                id="return_notes"
                value={returnForm.notes}
                onChange={(e) => setReturnForm({ ...returnForm, notes: e.target.value })}
                placeholder="Any additional information..."
                rows={2}
              />
            </div>

            <div className="border-t pt-4">
              <Label className="text-lg font-semibold mb-4 block">Select Materials to Return *</Label>
              {returnForm.project_id ? (
                <MaterialOrderForm
                  projects={[projects.find(p => p.id === returnForm.project_id)].filter(Boolean)}
                  mode="return"
                  hideProjectSelect={true}
                  defaultProjectId={returnForm.project_id}
                  customOrderTypes={[
                    { value: 'supplier_pickup', label: 'Supplier Pick Up' },
                    { value: 'own_vehicle_office', label: 'Own Vehicle Drop to Office' },
                    { value: 'own_vehicle_supplier', label: 'Own Vehicle Drop to Supplier Location' },
                    { value: 'company_truck', label: 'Company Truck Pick Up' }
                  ]}
                  submitButtonText="Submit Return"
                  onSubmit={(data) => {
                    setReturnForm({ ...returnForm, materials_description: JSON.stringify(data.insulation_items) });
                  }}
                  onCancel={() => {}}
                />
              ) : (
                <p className="text-gray-500 italic">Please select a project first</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowReturnDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitReturn}
                disabled={!returnForm.project_id || !returnForm.reason || !returnForm.materials_description}
              >
                Submit Return Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Material Transfer Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Material Transfer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="source_project">From Project *</Label>
                <ProjectCombobox
                  projects={allProjects}
                  value={transferForm.source_project_id}
                  onChange={(value) => setTransferForm({ ...transferForm, source_project_id: value })}
                  placeholder="Select source project"
                  filterActive={false}
                  testId="transfer-source-project"
                />
              </div>

              <div>
                <Label htmlFor="dest_project">To Project *</Label>
                <ProjectCombobox
                  projects={allProjects.filter(p => p.id !== transferForm.source_project_id)}
                  value={transferForm.destination_project_id}
                  onChange={(value) => setTransferForm({ ...transferForm, destination_project_id: value })}
                  placeholder="Select destination project"
                  filterActive={false}
                  testId="transfer-dest-project"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="transfer_reason">Reason for Transfer</Label>
              <Textarea
                id="transfer_reason"
                value={transferForm.reason}
                onChange={(e) => setTransferForm({ ...transferForm, reason: e.target.value })}
                placeholder="Why are you transferring these materials?"
                rows={2}
              />
            </div>

            <div className="border-t pt-4">
              <Label className="text-lg font-semibold mb-4 block">Select Materials to Transfer *</Label>
              {transferForm.destination_project_id ? (
                <MaterialOrderForm
                  projects={[allProjects.find(p => p.id === transferForm.destination_project_id)].filter(Boolean)}
                  mode="transfer"
                  hideProjectSelect={true}
                  defaultProjectId={transferForm.destination_project_id}
                  selectedToProject={allProjects.find(p => p.id === transferForm.destination_project_id)}
                  fromProject={allProjects.find(p => p.id === transferForm.source_project_id)}
                  customOrderTypes={[
                    { value: 'own_vehicle_other_project', label: 'Own Vehicle Drop to Other Project' },
                    { value: 'company_truck', label: 'Company Truck Pick Up' }
                  ]}
                  submitButtonText="Submit Transfer"
                  onSubmit={(data) => {
                    setTransferForm({ ...transferForm, materials_description: JSON.stringify(data.insulation_items) });
                  }}
                  onCancel={() => {}}
                />
              ) : (
                <p className="text-gray-500 italic">Please select both projects first</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowTransferDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitTransfer}
                disabled={!transferForm.source_project_id || !transferForm.destination_project_id || !transferForm.materials_description}
              >
                Submit Transfer Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* My Projects Dialog */}
      <Dialog open={showProjectDetailsDialog} onOpenChange={setShowProjectDetailsDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>My Projects</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Project Selector */}
            <div className="border-b pb-4">
              <Label className="text-lg font-semibold mb-3 block">Select a Project</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {projects.map(project => (
                  <div
                    key={project.id}
                    onClick={() => setSelectedProject(project)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedProject?.id === project.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <h3 className="font-semibold">{project.name}</h3>
                    <p className="text-sm text-gray-600">{project.address}</p>
                    <Badge variant={project.status === 'active' ? 'default' : 'secondary'} className="mt-2">
                      {project.status || 'active'}
                    </Badge>
                  </div>
                ))}
                {projects.length === 0 && (
                  <p className="text-gray-500 italic col-span-3">No projects assigned to you</p>
                )}
              </div>
            </div>

            {/* Project Details with Tabs */}
            {selectedProject && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h2 className="text-xl font-bold mb-2">{selectedProject.name}</h2>
                  <p className="text-gray-600">{selectedProject.address}</p>
                </div>

                <Tabs defaultValue="orders" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="orders" className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Orders
                    </TabsTrigger>
                    <TabsTrigger value="returns" className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Returns
                    </TabsTrigger>
                    <TabsTrigger value="transfers" className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Transfers
                    </TabsTrigger>
                    <TabsTrigger value="messages" className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Messages
                    </TabsTrigger>
                  </TabsList>

                  {/* Material Orders Tab */}
                  <TabsContent value="orders" className="space-y-3 mt-4">
                    <h3 className="text-lg font-semibold">My Material Orders</h3>
                    <div className="space-y-2">
                      {materialOrders
                        .filter(order => order.project_id === selectedProject.id)
                        .map(order => (
                          <div 
                            key={order.id}
                            className="p-3 border rounded bg-white hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowOrderDetails(true);
                            }}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold">Order #{order.id.substring(0, 8)}</p>
                                <p className="text-sm text-gray-600">
                                  {new Date(order.order_date).toLocaleDateString()}
                                </p>
                              </div>
                              <Badge variant={
                                order.status === 'pending' ? 'warning' :
                                order.status === 'ordered' ? 'default' :
                                order.status === 'received_at_site' ? 'success' :
                                'secondary'
                              }>
                                {order.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      {materialOrders.filter(o => o.project_id === selectedProject.id).length === 0 && (
                        <p className="text-gray-500 text-sm italic">No material orders for this project</p>
                      )}
                    </div>
                  </TabsContent>

                  {/* Material Returns Tab */}
                  <TabsContent value="returns" className="space-y-3 mt-4">
                    <h3 className="text-lg font-semibold">My Return Requests</h3>
                    <div className="space-y-2">
                      {materialReturns
                        .filter(returnReq => returnReq.project_id === selectedProject.id)
                        .map(returnReq => (
                          <div key={returnReq.id} className="p-3 border rounded bg-white">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold">Return Request</p>
                                <p className="text-sm text-gray-600">
                                  Reason: {returnReq.reason.replace(/_/g, ' ')}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {new Date(returnReq.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <Badge variant={
                                returnReq.status === 'approved' ? 'success' :
                                returnReq.status === 'rejected' ? 'destructive' :
                                'warning'
                              }>
                                {returnReq.status}
                              </Badge>
                            </div>
                            {returnReq.rejection_reason && (
                              <p className="text-sm text-red-600 mt-2">
                                Rejected: {returnReq.rejection_reason}
                              </p>
                            )}
                          </div>
                        ))}
                      {materialReturns.filter(r => r.project_id === selectedProject.id).length === 0 && (
                        <p className="text-gray-500 text-sm italic">No return requests for this project</p>
                      )}
                    </div>
                  </TabsContent>

                  {/* Material Transfers Tab */}
                  <TabsContent value="transfers" className="space-y-3 mt-4">
                    <h3 className="text-lg font-semibold">Material Transfers</h3>
                    <div className="space-y-2">
                      {materialTransfers
                        .filter(transfer => 
                          transfer.source_project_id === selectedProject.id || 
                          transfer.destination_project_id === selectedProject.id
                        )
                        .map(transfer => {
                          const sourceProj = allProjects.find(p => p.id === transfer.source_project_id) || projects.find(p => p.id === transfer.source_project_id);
                          const destProj = allProjects.find(p => p.id === transfer.destination_project_id) || projects.find(p => p.id === transfer.destination_project_id);
                          const isSource = transfer.source_project_id === selectedProject.id;
                          
                          return (
                            <div key={transfer.id} className="p-3 border rounded bg-white">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-semibold">
                                    {isSource ? '🔴 Transfer Out' : '🟢 Transfer In'}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {sourceProj?.name || 'Unknown'} → {destProj?.name || 'Unknown'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {new Date(transfer.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <Badge variant={
                                  transfer.status === 'approved' ? 'success' :
                                  transfer.status === 'rejected' ? 'destructive' :
                                  'warning'
                                }>
                                  {transfer.status}
                                </Badge>
                              </div>
                              {transfer.rejection_reason && (
                                <p className="text-sm text-red-600 mt-2">
                                  Rejected: {transfer.rejection_reason}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      {materialTransfers.filter(t => 
                        t.source_project_id === selectedProject.id || 
                        t.destination_project_id === selectedProject.id
                      ).length === 0 && (
                        <p className="text-gray-500 text-sm italic">No transfers for this project</p>
                      )}
                    </div>
                  </TabsContent>

                  {/* Project Messages Tab */}
                  <TabsContent value="messages" className="space-y-3 mt-4">
                    <h3 className="text-lg font-semibold">Project Chat & Communication</h3>
                    
                    {/* Messages Display */}
                    <div className="max-h-96 overflow-y-auto space-y-2 bg-gray-50 p-4 rounded-lg">
                      {messages
                        .filter(msg => msg.project_id === selectedProject.id)
                        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                        .slice(0, 20)
                        .map(msg => (
                          <div key={msg.id} className="p-3 bg-white rounded border">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-semibold text-sm">
                                {msg.sender_name || 'Unknown'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(msg.timestamp).toLocaleString()}
                              </span>
                            </div>
                            
                            {/* Show if replying to someone */}
                            {msg.reply_to_sender && (
                              <div className="mb-2 pl-3 border-l-2 border-blue-400 bg-blue-50 p-2 rounded text-xs">
                                <p className="font-semibold text-blue-700">Replying to {msg.reply_to_sender}</p>
                                <p className="text-gray-600 italic">{msg.reply_to_message}</p>
                              </div>
                            )}
                            
                            <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                            <div className="flex gap-2 items-center mt-1">
                              {msg.message_type && msg.message_type !== 'chat' && (
                                <Badge variant="secondary" className="text-xs">
                                  {msg.message_type}
                                </Badge>
                              )}
                              {msg.sub_project_id && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                                  📂 Sub-project
                                </Badge>
                              )}
                            </div>
                            
                            {/* Display images if present */}
                            {msg.image_urls && msg.image_urls.length > 0 && (
                              <div className="mt-2 flex gap-2 flex-wrap">
                                {msg.image_urls.map((url, idx) => {
                                  // Convert to API endpoint URL
                                  const filename = url.split('/').pop();
                                  const displayUrl = `${API_URL}/api/image/${filename}`;
                                  return (
                                    <img 
                                      key={idx} 
                                      src={displayUrl}
                                      alt="Attachment" 
                                      className="w-32 h-32 object-cover rounded border cursor-pointer hover:opacity-80"
                                      onClick={() => {
                                        setViewerImages(msg.image_urls);
                                        setViewerIndex(idx);
                                        setShowImageViewer(true);
                                      }}
                                    />
                                  );
                                })}
                              </div>
                            )}
                            
                            {/* Display video if present */}
                            {msg.video_url && (
                              <div className="mt-2">
                                <video controls className="w-full max-w-md rounded border">
                                  <source src={msg.video_url} type="video/mp4" />
                                  Your browser does not support video playback.
                                </video>
                              </div>
                            )}
                            
                            {/* Display Media Tags */}
                            {msg.media_tags && msg.media_tags.length > 0 && (
                              <div className="mt-2 flex gap-1 flex-wrap">
                                {msg.media_tags.map((tag, idx) => (
                                  <span 
                                    key={idx}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                                  >
                                    🏷️ {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                            
                            {/* Display Media Comments */}
                            {msg.media_comments && Object.keys(msg.media_comments).length > 0 && (
                              <div className="mt-2 p-2 bg-gray-50 rounded border-l-4 border-blue-400">
                                <p className="text-xs font-semibold text-gray-700">Media Notes:</p>
                                {Object.entries(msg.media_comments).map(([key, comment], idx) => (
                                  <p key={idx} className="text-xs text-gray-600 mt-1">
                                    💬 {comment}
                                  </p>
                                ))}
                              </div>
                            )}
                            
                            {/* Reply Button */}
                            <button
                              onClick={() => {
                                setReplyingToMessage(msg);
                                setProjectMessageText('');
                                // Scroll to input
                                document.querySelector('#employee-message-input')?.scrollIntoView({ behavior: 'smooth' });
                              }}
                              className="mt-2 text-xs text-blue-600 hover:text-blue-800"
                            >
                              ↩️ Reply
                            </button>
                          </div>
                        ))}
                      {messages.filter(m => m.project_id === selectedProject.id).length === 0 && (
                        <p className="text-gray-500 text-sm italic">No messages for this project</p>
                      )}
                    </div>
                    
                    {/* Send Message Form */}
                    <form onSubmit={handleSendProjectMessage} className="space-y-3 border-t pt-3">
                      {/* Show who we're replying to */}
                      {replyingToMessage && (
                        <div className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded">
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-blue-700">Replying to {replyingToMessage.sender_name}</p>
                            <p className="text-xs text-gray-600 italic">{replyingToMessage.message.substring(0, 60)}...</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setReplyingToMessage(null)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                      
                      <Textarea
                        id="employee-message-input"
                        placeholder={replyingToMessage ? `Reply to ${replyingToMessage.sender_name}...` : "Type your message..."}
                        value={projectMessageText}
                        onChange={(e) => setProjectMessageText(e.target.value)}
                        rows={3}
                      />
                      
                      {/* Image Preview */}
                      {projectMessageImages.length > 0 && (
                        <div className="flex gap-2 flex-wrap">
                          {projectMessageImages.map((url, idx) => (
                            <div key={idx} className="relative">
                              <img src={url} alt="Preview" className="w-20 h-20 object-cover rounded border" />
                              <button
                                type="button"
                                onClick={() => setProjectMessageImages(projectMessageImages.filter((_, i) => i !== idx))}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Video Preview */}
                      {projectMessageVideo && (
                        <div className="relative inline-block">
                          <video src={projectMessageVideo} className="w-40 h-28 rounded border" controls />
                          <button
                            type="button"
                            onClick={() => setProjectMessageVideo(null)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                          >
                            ×
                          </button>
                        </div>
                      )}
                      
                      {/* Sub-project Selection (when media is present) */}
                      {(projectMessageImages.length > 0 || projectMessageVideo) && (
                        <div className="space-y-2 p-3 bg-gray-50 rounded border">
                          <div>
                            <Label className="text-xs">Sub-project (Optional)</Label>
                            <select
                              className="w-full border rounded px-2 py-1 text-sm mt-1"
                              value={selectedSubProject?.id || ''}
                              onChange={(e) => {
                                const subProj = subProjects.find(sp => sp.id === e.target.value);
                                setSelectedSubProject(subProj || null);
                              }}
                              onClick={() => {
                                if (subProjects.length === 0 && selectedProject) {
                                  fetchSubProjects(selectedProject.id);
                                }
                              }}
                            >
                              <option value="">None</option>
                              {subProjects.map(sp => (
                                <option key={sp.id} value={sp.id}>{sp.name}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <Label className="text-xs">Tags (comma-separated)</Label>
                            <Input
                              type="text"
                              placeholder="e.g., inspection, progress, issue"
                              value={mediaTags}
                              onChange={(e) => setMediaTags(e.target.value)}
                              className="text-sm"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Auto-tags: {selectedProject?.name}{selectedSubProject ? `, ${selectedSubProject.name}` : ''}
                            </p>
                          </div>
                          
                          <div>
                            <Label className="text-xs">General Comment (Optional)</Label>
                            <Input
                              type="text"
                              placeholder="Add a comment for this media"
                              value={mediaComments.general || ''}
                              onChange={(e) => setMediaComments({...mediaComments, general: e.target.value})}
                              className="text-sm"
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Upload Buttons and Send */}
                      <div className="flex gap-2 items-center">
                        <input
                          type="file"
                          id="project-images"
                          accept="image/*"
                          multiple
                          onChange={handleProjectImageUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="project-images"
                          className="cursor-pointer px-3 py-2 border rounded text-sm hover:bg-gray-50 flex items-center gap-1"
                        >
                          📷 Photos
                        </label>
                        
                        <input
                          type="file"
                          id="project-video"
                          accept="video/*"
                          onChange={handleProjectVideoUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="project-video"
                          className="cursor-pointer px-3 py-2 border rounded text-sm hover:bg-gray-50 flex items-center gap-1"
                        >
                          🎥 Video
                        </label>
                        
                        {uploadingMedia && (
                          <span className="text-xs text-gray-500">Uploading...</span>
                        )}
                        
                        <Button type="submit" size="sm" className="ml-auto" disabled={uploadingMedia}>
                          Send Message
                        </Button>
                      </div>
                    </form>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Viewer Dialog */}
      <ImageViewerDialog 
        open={showImageViewer}
        onOpenChange={setShowImageViewer}
        images={viewerImages}
        currentIndex={viewerIndex}
        onIndexChange={setViewerIndex}
      />

      {/* Work Progress Dialog */}
      <WorkProgressDialog 
        open={showWorkProgressDialog}
        onOpenChange={setShowWorkProgressDialog}
        currentLocation={currentLocation}
        onClockOutSuccess={handleClockOutSuccess}
      />

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Poppins:wght@600;700;800&display=swap');

        .employee-dashboard {
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          padding: 0;
        }

        .modern-bg {
          background: linear-gradient(135deg, #5b63d3 0%, #6b2fa0 100%);
          background-attachment: fixed;
        }

        /* Company Header styles moved to /src/styles/companyHeader.css */

        .logout-btn {
          background: white;
          border: 1px solid #e2e8f0;
          font-weight: 500;
        }

        /* Dashboard Header */
        .dashboard-header-modern {
          background: white;
          margin: 2rem;
          padding: 2rem;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .personalized-title {
          font-size: 2.5rem;
          font-weight: 800;
          background: linear-gradient(135deg, #5b63d3 0%, #6b2fa0 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0;
          font-family: 'Poppins', sans-serif;
        }

        .welcome-text {
          font-size: 1rem;
          color: #64748b;
          margin: 0.5rem 0 0 0;
          font-weight: 500;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          padding: 1.5rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .dashboard-header h1 {
          font-size: 1.875rem;
          font-weight: 700;
          color: #1e293b;
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          margin: 0 2rem 2rem 2rem;
        }

        /* Main Content Area */
        .dashboard-grid > * {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .dashboard-grid > *:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }

        /* Quick Stats Cards */
        .quick-stats-grid {
          margin: 0 2rem;
        }

        .stat-card {
          border: none;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18);
        }

        .stat-card-green {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }

        .stat-card-green p,
        .stat-card-green .text-gray-700,
        .stat-card-green .text-green-800 {
          color: white !important;
        }

        .stat-card-blue {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }

        .stat-card-blue p,
        .stat-card-blue .text-gray-700,
        .stat-card-blue .text-blue-800 {
          color: white !important;
        }

        .stat-card-purple {
          background: linear-gradient(135deg, #a855f7 0%, #9333ea 100%);
          color: white;
        }

        .stat-card-purple p,
        .stat-card-purple .text-gray-700,
        .stat-card-purple .text-purple-800 {
          color: white !important;
        }

        .stat-card-gray {
          background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
          color: white;
        }

        .stat-card-gray p,
        .stat-card-gray .text-gray-700,
        .stat-card-gray .text-gray-500 {
          color: white !important;
        }

        /* Action Buttons */
        .action-buttons-container {
          margin: 0 2rem 2rem 2rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        @media (min-width: 768px) {
          .action-buttons-container {
            flex-direction: row;
          }
        }

        .action-btn {
          flex: 1;
          font-weight: 600;
          font-size: 1rem;
          padding: 1.5rem;
          border-radius: 12px;
          transition: all 0.3s;
        }

        .action-btn-primary {
          background: linear-gradient(135deg, #5b63d3 0%, #6b2fa0 100%);
          border: none;
          box-shadow: 0 4px 15px rgba(91, 99, 211, 0.5);
        }

        .action-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(91, 99, 211, 0.7);
          filter: brightness(1.1);
        }

        .action-btn-secondary {
          background: white;
          border: 2px solid #5b63d3;
          color: #5b63d3;
          font-weight: 600;
        }

        .action-btn-secondary:hover {
          background: #5b63d3;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(91, 99, 211, 0.4);
        }

        .time-tracking {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .time-entries {
          margin-top: 1rem;
        }

        .time-entries h4 {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
          margin-bottom: 0.75rem;
        }

        .time-entry {
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 6px;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }

        .schedules-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .schedule-item {
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
        }

        .schedule-date {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .schedule-time {
          color: #64748b;
          font-size: 0.875rem;
        }

        .schedule-notes {
          margin-top: 0.5rem;
          color: #64748b;
          font-size: 0.875rem;
          font-style: italic;
        }

        .tasks-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .task-item {
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
        }

        .task-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .task-due {
          font-size: 0.875rem;
          color: #64748b;
          margin-top: 0.5rem;
        }

        .task-actions {
          margin-top: 0.75rem;
        }

        .messages-container {
          max-height: 300px;
          overflow-y: auto;
          margin-bottom: 1rem;
        }

        .message {
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 6px;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }

        .message-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.25rem;
        }

        .message-header span {
          color: #64748b;
          font-size: 0.75rem;
        }

        .message-form {
          display: flex;
          gap: 0.5rem;
        }

        /* ====================================
           MOBILE RESPONSIVE STYLES
           ==================================== */
        
        @media (max-width: 768px) {
          /* Dashboard Layout */
          .employee-dashboard {
            padding: 0.5rem;
            min-height: 100vh;
          }

          /* Header */
          .dashboard-header {
            flex-direction: column;
            gap: 0.75rem;
            padding: 1rem;
            margin: 0 0 1rem 0;
          }

          .dashboard-header h1 {
            font-size: 1.5rem;
            text-align: center;
          }

          .dashboard-header p {
            font-size: 0.875rem;
            text-align: center;
          }

          .header-actions {
            width: 100%;
            justify-content: center;
          }

          /* Company Logo */
          .company-logo {
            width: 50px !important;
            height: 50px !important;
          }

          /* Quick Stats Grid */
          .quick-stats-grid {
            grid-template-columns: 1fr !important;
            gap: 0.75rem !important;
            margin: 0 0.5rem 1rem 0.5rem !important;
          }

          .stat-card {
            padding: 1rem !important;
          }

          .stat-card h3 {
            font-size: 1.75rem !important;
          }

          .stat-card p {
            font-size: 0.875rem !important;
          }

          /* Action Buttons */
          .action-buttons-container {
            margin: 0 0.5rem 1rem 0.5rem !important;
            gap: 0.75rem !important;
          }

          .action-btn {
            padding: 1rem !important;
            font-size: 0.875rem !important;
            min-height: 60px;
          }

          /* Dashboard Grid */
          .dashboard-grid {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
            margin: 0 0.5rem !important;
          }

          .dashboard-grid > * {
            padding: 1rem !important;
          }

          /* Cards */
          .dashboard-grid .card {
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08) !important;
          }

          /* Time Tracking */
          .time-tracking {
            gap: 0.75rem !important;
          }

          /* Time Entries */
          .time-entry {
            padding: 0.75rem !important;
            font-size: 0.8125rem !important;
          }

          /* Tasks List */
          .tasks-list {
            gap: 0.75rem !important;
          }

          .task-item {
            padding: 0.75rem !important;
          }

          /* Messages */
          .messages-container {
            max-height: 250px !important;
          }

          .message {
            padding: 0.75rem !important;
            font-size: 0.8125rem !important;
          }

          .message-form {
            flex-direction: column !important;
          }

          .message-form textarea {
            min-height: 80px !important;
          }

          .message-form button {
            width: 100% !important;
          }

          /* Material Orders */
          .orders-list {
            gap: 0.75rem !important;
          }

          .order-item {
            padding: 0.75rem !important;
          }

          /* Schedules */
          .schedules-list {
            gap: 0.75rem !important;
          }

          .schedule-item {
            padding: 0.75rem !important;
          }

          /* Tabs */
          .tabs-list {
            flex-wrap: wrap !important;
            gap: 0.5rem !important;
          }

          .tabs-trigger {
            flex: 1 1 45% !important;
            min-width: 120px !important;
          }

          /* Buttons */
          button {
            min-height: 44px !important; /* Touch-friendly */
            padding: 0.75rem 1rem !important;
          }

          /* Input Fields */
          input, textarea, select {
            font-size: 16px !important; /* Prevents zoom on iOS */
            min-height: 44px !important;
          }

          /* Logout Button */
          .header-actions button {
            padding: 0.5rem 1rem !important;
            font-size: 0.875rem !important;
          }

          /* Dialogs */
          .dialog-content {
            max-width: 95vw !important;
            max-height: 90vh !important;
            overflow-y: auto !important;
          }

          /* Material Order Form */
          .material-order-form {
            padding: 1rem !important;
          }

          /* Expense Form */
          .expense-form {
            gap: 1rem !important;
          }

          /* Tables - Convert to Cards on Mobile */
          table {
            display: block !important;
            overflow-x: auto !important;
          }

          th, td {
            font-size: 0.8125rem !important;
            padding: 0.5rem !important;
          }
        }

        /* Extra Small Screens (< 375px) */
        @media (max-width: 375px) {
          .dashboard-header h1 {
            font-size: 1.25rem !important;
          }

          .stat-card h3 {
            font-size: 1.5rem !important;
          }

          .action-btn {
            font-size: 0.8125rem !important;
            padding: 0.875rem !important;
          }

          .quick-stats-grid,
          .action-buttons-container,
          .dashboard-grid {
            margin: 0 0.25rem !important;
          }
        }

        /* Landscape Orientation on Mobile */
        @media (max-height: 500px) and (orientation: landscape) {
          .dashboard-header {
            padding: 0.5rem !important;
          }

          .dashboard-header h1 {
            font-size: 1.25rem !important;
          }

          .stat-card {
            padding: 0.75rem !important;
          }

          .action-btn {
            padding: 0.75rem !important;
          }
        }
      `}</style>

      {/* Change Password Dialog */}
      <ChangePasswordDialog 
        open={showChangePasswordDialog} 
        onOpenChange={setShowChangePasswordDialog} 
      />

      {/* Bi-Weekly Hours Dialog */}
      <Dialog open={showBiweeklyDialog} onOpenChange={setShowBiweeklyDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="biweekly-hours-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-700" />
              Your Bi-Weekly Hours
            </DialogTitle>
          </DialogHeader>
          {biweeklyLoading ? (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : biweeklyData ? (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                <p className="text-xs text-green-700 uppercase tracking-wide">14-day total</p>
                <p className="text-2xl font-bold text-green-900">{biweeklyData.total_hours} hrs</p>
              </div>
              <EmployeeDaysBreakdown data={biweeklyData} formatDateDisplay={(d) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} isAdmin={false} />
            </>
          ) : (
            <p className="text-center text-gray-500 py-6">No hours data available.</p>
          )}
        </DialogContent>
      </Dialog>

      {/* My Profile Dialog */}
      <Dialog open={showMyProfileDialog} onOpenChange={setShowMyProfileDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="my-profile-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <User className="w-6 h-6" />
              My Profile
              {isFirstTimeUser && (
                <Badge variant="warning" className="ml-2">Please Complete</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="personal" className="w-full">
            {/* On phones the 5 labels overlap horizontally — switch to a 2-row
                grid (3 + 2) with shorter labels + icons. Desktop keeps one
                row of 5. */}
            <TabsList className="grid w-full grid-cols-3 sm:grid-cols-5 mb-4 h-auto p-1 gap-1">
              <TabsTrigger value="personal" data-testid="personal-tab" className="text-[11px] sm:text-sm px-1.5 py-2 flex flex-col sm:flex-row items-center gap-0.5 sm:gap-2 leading-tight">
                <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                <span className="truncate">Personal</span>
              </TabsTrigger>
              <TabsTrigger value="certificates" data-testid="certificates-tab" className="text-[11px] sm:text-sm px-1.5 py-2 flex flex-col sm:flex-row items-center gap-0.5 sm:gap-2 leading-tight">
                <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                <span className="truncate">Certs{myProfile?.certificates?.length > 0 ? ` (${myProfile.certificates.length})` : ''}</span>
              </TabsTrigger>
              <TabsTrigger value="licenses" data-testid="licenses-tab" className="text-[11px] sm:text-sm px-1.5 py-2 flex flex-col sm:flex-row items-center gap-0.5 sm:gap-2 leading-tight">
                <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                <span className="truncate">Licenses{myProfile?.licenses?.length > 0 ? ` (${myProfile.licenses.length})` : ''}</span>
              </TabsTrigger>
              <TabsTrigger value="credentials" data-testid="credentials-tab" className="text-[11px] sm:text-sm px-1.5 py-2 flex flex-col sm:flex-row items-center gap-0.5 sm:gap-2 leading-tight">
                <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                <span className="truncate">Creds{myProfile?.credentials?.length > 0 ? ` (${myProfile.credentials.length})` : ''}</span>
              </TabsTrigger>
              <TabsTrigger value="earnings" data-testid="earnings-tab" className="text-[11px] sm:text-sm px-1.5 py-2 flex flex-col sm:flex-row items-center gap-0.5 sm:gap-2 leading-tight">
                <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                <span className="truncate">Earnings</span>
              </TabsTrigger>
            </TabsList>

            {/* Personal Details Tab */}
            <TabsContent value="personal" className="space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h4 className="font-semibold text-indigo-900 mb-2">Personal Information</h4>
                <p className="text-sm text-indigo-700">Keep your personal details up to date for payroll and emergency contacts.</p>
              </div>
              <Button 
                onClick={() => setShowPersonalDetailsDialog(true)} 
                className="w-full"
                data-testid="edit-personal-details-btn"
              >
                <User className="w-4 h-4 mr-2" />
                Edit Personal Details
              </Button>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Name</p>
                  <p className="font-medium">{user?.name || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">{user?.email || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium">{user?.phone || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Legal Status</p>
                  <p className="font-medium">{user?.personal_details?.legal_status?.replace('_', ' ') || 'Not set'}</p>
                </div>
              </div>
            </TabsContent>

            {/* Certificates Tab */}
            <TabsContent value="certificates" className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-900 mb-2">Required Certificates</h4>
                <p className="text-sm text-blue-700">Please upload the following mandatory certificates. Optional certificates help demonstrate additional qualifications.</p>
              </div>

              {/* Predefined Certificate List */}
              <div className="space-y-3">
                {[
                  { name: 'WHMIS', mandatory: true },
                  { name: 'Working at Heights', mandatory: true },
                  { name: 'Fall Protection', mandatory: true },
                  { name: 'Elevated Platform', mandatory: true },
                  { name: 'Worker Health & Safety Awareness', mandatory: true },
                  { name: 'Confined Space Entry', mandatory: false },
                  { name: 'Forklift Operator', mandatory: false },
                  { name: 'Scaffold Safety', mandatory: false },
                  { name: 'First Aid/CPR', mandatory: false },
                  { name: 'Boom Lift Operator', mandatory: false },
                  { name: 'Supervisor Health & Safety Awareness', mandatory: false },
                ].map((certTemplate, idx) => {
                  const existingCert = myProfile?.certificates?.find(c => 
                    c.name.toLowerCase().includes(certTemplate.name.toLowerCase().split(' ')[0].toLowerCase())
                  );
                  
                  // Check if certificate is expired
                  const isExpired = existingCert?.expiry_date && new Date(existingCert.expiry_date) < new Date();
                  
                  return (
                    <div 
                      key={idx} 
                      className={`p-4 border-2 rounded-lg ${
                        isExpired 
                          ? 'bg-red-100 border-red-500 animate-pulse' 
                          : existingCert 
                            ? 'bg-green-50 border-green-200' 
                            : certTemplate.mandatory 
                              ? 'bg-red-50 border-red-200' 
                              : 'bg-gray-50 border-gray-200'
                      }`}
                      data-testid={`cert-item-${idx}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{certTemplate.name}</span>
                            {certTemplate.mandatory ? (
                              <Badge variant="destructive" className="text-xs">Mandatory</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Optional</Badge>
                            )}
                            {isExpired ? (
                              <Badge className="text-xs bg-red-600 text-white animate-pulse">⚠️ EXPIRED</Badge>
                            ) : existingCert ? (
                              <Badge variant="success" className="text-xs bg-green-600 text-white">✓ Valid</Badge>
                            ) : null}
                          </div>
                          
                          {existingCert && (
                            <div className="mt-2 text-sm">
                              <p className="text-gray-600">Certificate #: {existingCert.certificate_number || 'N/A'}</p>
                              {existingCert.issue_date && (
                                <p className="text-gray-600">
                                  Issued: {new Date(existingCert.issue_date).toLocaleDateString()}
                                </p>
                              )}
                              {existingCert.expiry_date && (
                                <p className={isExpired ? 'text-red-700 font-bold' : 'text-gray-600'}>
                                  Expires: {new Date(existingCert.expiry_date).toLocaleDateString()}
                                  {isExpired && ' - ACTION REQUIRED: Upload new certificate'}
                                </p>
                              )}
                              {existingCert.issuing_authority && (
                                <p className="text-gray-500 text-xs">
                                  Issued by: {existingCert.issuing_authority}
                                </p>
                              )}
                              {/* Document attachment */}
                              {existingCert.document_url ? (
                                <div className="mt-2 flex items-center gap-2">
                                  <a 
                                    href={`${API_URL}${existingCert.document_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                                    data-testid={`view-doc-${idx}`}
                                  >
                                    <FileText className="w-3 h-3" />
                                    View Document
                                  </a>
                                </div>
                              ) : (
                                <div className="mt-2">
                                  <label className="cursor-pointer text-xs text-blue-600 hover:underline flex items-center gap-1">
                                    <Upload className="w-3 h-3" />
                                    Attach Document (PDF, JPG, PNG)
                                    <input
                                      type="file"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      className="hidden"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          uploadCertificateDocument(existingCert.id, file);
                                        }
                                      }}
                                      data-testid={`upload-doc-${idx}`}
                                    />
                                  </label>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          {existingCert ? (
                            <div className="flex flex-col gap-1">
                              {isExpired && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700"
                                  onClick={() => {
                                    setNewCertificate({ ...newCertificate, name: certTemplate.name });
                                    setShowAddCertificateDialog(true);
                                  }}
                                  data-testid={`update-cert-${idx}`}
                                >
                                  Update
                                </Button>
                              )}
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => deleteMyCertificate(existingCert.id)}
                                data-testid={`delete-cert-${idx}`}
                              >
                                Remove
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setNewCertificate({ ...newCertificate, name: certTemplate.name });
                                setShowAddCertificateDialog(true);
                              }}
                              data-testid={`add-cert-${idx}`}
                            >
                              + Add
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Custom Certificates */}
              {myProfile?.certificates?.filter(c => 
                !['whmis', 'working at heights', 'first aid', 'fall protection', 'confined space', 'forklift', 'scaffold', 'asbestos', 'fire safety', 'electrical'].some(
                  keyword => c.name.toLowerCase().includes(keyword)
                )
              ).length > 0 && (
                <div className="mt-6">
                  <h4 className="font-semibold mb-3">Other Certificates</h4>
                  <div className="space-y-2">
                    {myProfile.certificates
                      .filter(c => 
                        !['whmis', 'working at heights', 'first aid', 'fall protection', 'confined space', 'forklift', 'scaffold', 'asbestos', 'fire safety', 'electrical'].some(
                          keyword => c.name.toLowerCase().includes(keyword)
                        )
                      )
                      .map((cert, idx) => (
                        <div key={cert.id} className="p-3 border rounded bg-white flex justify-between items-center">
                          <div>
                            <span className="font-medium">{cert.name}</span>
                            {cert.expiry_date && (
                              <span className="text-sm text-gray-500 ml-2">
                                Expires: {new Date(cert.expiry_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteMyCertificate(cert.id)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              <Button 
                className="w-full mt-4"
                onClick={() => {
                  setNewCertificate({ name: '', issuing_authority: '', issue_date: '', expiry_date: '', certificate_number: '' });
                  setShowAddCertificateDialog(true);
                }}
                data-testid="add-custom-certificate-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Other Certificate
              </Button>
            </TabsContent>

            {/* Licenses Tab */}
            <TabsContent value="licenses" className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-900 mb-2">Licenses</h4>
                <p className="text-sm text-blue-700">Add any professional or trade licenses you hold.</p>
              </div>

              {/* Existing Licenses */}
              {myProfile?.licenses?.length > 0 ? (
                <div className="space-y-3">
                  {myProfile.licenses.map((license, idx) => {
                    const isExpired = license.expiry_date && new Date(license.expiry_date) < new Date();
                    return (
                    <div key={license.id} className={`p-4 border rounded-lg ${isExpired ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-200'}`} data-testid={`license-item-${idx}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium">{license.license_type}</div>
                          <div className="text-sm text-gray-600">
                            <p>License #: {license.license_number}</p>
                            {license.state && <p>State/Province: {license.state}</p>}
                            {license.expiry_date && (
                              <p className={isExpired ? 'text-red-600 font-semibold' : ''}>
                                Expires: {new Date(license.expiry_date).toLocaleDateString()}
                                {isExpired && ' (EXPIRED)'}
                              </p>
                            )}
                          </div>
                          {/* Document Upload/View */}
                          {license.document_url ? (
                            <div className="mt-2 flex items-center gap-2">
                              <a 
                                href={`${API_URL}${license.document_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                              >
                                <FileText className="w-3 h-3" />
                                View Document
                              </a>
                            </div>
                          ) : (
                            <div className="mt-2">
                              <label className="cursor-pointer text-xs text-blue-600 hover:underline flex items-center gap-1">
                                <Upload className="w-3 h-3" />
                                Attach Document (PDF, JPG, PNG)
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) uploadLicenseDocument(license.id, file);
                                  }}
                                />
                              </label>
                            </div>
                          )}
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteMyLicense(license.id)}
                          data-testid={`delete-license-${idx}`}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  )})}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No licenses added yet</p>
                </div>
              )}

              <Button 
                className="w-full mt-4"
                onClick={() => {
                  setNewLicense({ license_type: '', license_number: '', state: '', issue_date: '', expiry_date: '' });
                  setShowAddLicenseDialog(true);
                }}
                data-testid="add-license-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add License
              </Button>
            </TabsContent>

            {/* Credentials Tab */}
            <TabsContent value="credentials" className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-900 mb-2">Professional Credentials</h4>
                <p className="text-sm text-blue-700">Add any other professional credentials, memberships, or qualifications.</p>
              </div>

              {/* Existing Credentials */}
              {myProfile?.credentials?.length > 0 ? (
                <div className="space-y-3">
                  {myProfile.credentials.map((credential, idx) => {
                    const isExpired = credential.expiry_date && new Date(credential.expiry_date) < new Date();
                    return (
                    <div key={credential.id} className={`p-4 border rounded-lg ${isExpired ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-200'}`} data-testid={`credential-item-${idx}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium">{credential.credential_type}</div>
                          <div className="text-sm text-gray-600">
                            {credential.credential_number && <p>ID/Number: {credential.credential_number}</p>}
                            {credential.expiry_date && (
                              <p className={isExpired ? 'text-red-600 font-semibold' : ''}>
                                Expires: {new Date(credential.expiry_date).toLocaleDateString()}
                                {isExpired && ' (EXPIRED)'}
                              </p>
                            )}
                            {credential.notes && <p className="italic">{credential.notes}</p>}
                          </div>
                          {/* Document Upload/View */}
                          {credential.document_url ? (
                            <div className="mt-2 flex items-center gap-2">
                              <a 
                                href={`${API_URL}${credential.document_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline flex items-center gap-1 text-xs"
                              >
                                <FileText className="w-3 h-3" />
                                View Document
                              </a>
                            </div>
                          ) : (
                            <div className="mt-2">
                              <label className="cursor-pointer text-xs text-blue-600 hover:underline flex items-center gap-1">
                                <Upload className="w-3 h-3" />
                                Attach Document (PDF, JPG, PNG)
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) uploadCredentialDocument(credential.id, file);
                                  }}
                                />
                              </label>
                            </div>
                          )}
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteMyCredential(credential.id)}
                          data-testid={`delete-credential-${idx}`}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  )})}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No credentials added yet</p>
                </div>
              )}

              <Button 
                className="w-full mt-4"
                onClick={() => {
                  setNewCredential({ credential_type: '', credential_number: '', issue_date: '', expiry_date: '', notes: '' });
                  setShowAddCredentialDialog(true);
                }}
                data-testid="add-credential-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Credential
              </Button>
            </TabsContent>

            {/* Earnings Tab */}
            <TabsContent value="earnings" className="space-y-4">
              <MyEarnings />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Add Certificate Dialog */}
      <Dialog open={showAddCertificateDialog} onOpenChange={setShowAddCertificateDialog}>
        <DialogContent className="max-w-md" data-testid="add-certificate-dialog">
          <DialogHeader>
            <DialogTitle>Add Certificate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Certificate Name *</Label>
              <Input
                value={newCertificate.name}
                onChange={(e) => setNewCertificate({ ...newCertificate, name: e.target.value })}
                placeholder="e.g., WHMIS Training"
                data-testid="cert-name-input"
              />
            </div>
            <div>
              <Label>Certificate Number</Label>
              <Input
                value={newCertificate.certificate_number}
                onChange={(e) => setNewCertificate({ ...newCertificate, certificate_number: e.target.value })}
                placeholder="e.g., CERT-12345"
                data-testid="cert-number-input"
              />
            </div>
            <div>
              <Label>Issuing Authority</Label>
              <Input
                value={newCertificate.issuing_authority}
                onChange={(e) => setNewCertificate({ ...newCertificate, issuing_authority: e.target.value })}
                placeholder="e.g., Ontario Ministry of Labour"
                data-testid="cert-authority-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={newCertificate.issue_date}
                  onChange={(e) => setNewCertificate({ ...newCertificate, issue_date: e.target.value })}
                  data-testid="cert-issue-date-input"
                />
              </div>
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={newCertificate.expiry_date}
                  onChange={(e) => setNewCertificate({ ...newCertificate, expiry_date: e.target.value })}
                  data-testid="cert-expiry-date-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAddCertificateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={addMyCertificate} disabled={!newCertificate.name} data-testid="save-certificate-btn">
                Save Certificate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add License Dialog */}
      <Dialog open={showAddLicenseDialog} onOpenChange={setShowAddLicenseDialog}>
        <DialogContent className="max-w-md" data-testid="add-license-dialog">
          <DialogHeader>
            <DialogTitle>Add License</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>License Type *</Label>
              <Select
                value={newLicense.license_type}
                onValueChange={(val) => setNewLicense({ ...newLicense, license_type: val })}
              >
                <SelectTrigger data-testid="license-type-select">
                  <SelectValue placeholder="Select license type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Driver's License - Class G">Driver's License - Class G</SelectItem>
                  <SelectItem value="Driver's License - Class G2">Driver's License - Class G2</SelectItem>
                  <SelectItem value="Driver's License - Class AZ">Driver's License - Class AZ</SelectItem>
                  <SelectItem value="Driver's License - Class DZ">Driver's License - Class DZ</SelectItem>
                  <SelectItem value="Trade License - Plumber">Trade License - Plumber</SelectItem>
                  <SelectItem value="Trade License - Electrician">Trade License - Electrician</SelectItem>
                  <SelectItem value="Trade License - HVAC">Trade License - HVAC</SelectItem>
                  <SelectItem value="Trade License - Insulation Mechanic">Trade License - Insulation Mechanic</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>License Number *</Label>
              <Input
                value={newLicense.license_number}
                onChange={(e) => setNewLicense({ ...newLicense, license_number: e.target.value })}
                placeholder="e.g., A1234-56789"
                data-testid="license-number-input"
              />
            </div>
            <div>
              <Label>State/Province</Label>
              <Input
                value={newLicense.state}
                onChange={(e) => setNewLicense({ ...newLicense, state: e.target.value })}
                placeholder="e.g., Ontario"
                data-testid="license-state-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={newLicense.issue_date}
                  onChange={(e) => setNewLicense({ ...newLicense, issue_date: e.target.value })}
                  data-testid="license-issue-date-input"
                />
              </div>
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={newLicense.expiry_date}
                  onChange={(e) => setNewLicense({ ...newLicense, expiry_date: e.target.value })}
                  data-testid="license-expiry-date-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAddLicenseDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={addMyLicense} 
                disabled={!newLicense.license_type || !newLicense.license_number}
                data-testid="save-license-btn"
              >
                Save License
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Credential Dialog */}
      <Dialog open={showAddCredentialDialog} onOpenChange={setShowAddCredentialDialog}>
        <DialogContent className="max-w-md" data-testid="add-credential-dialog">
          <DialogHeader>
            <DialogTitle>Add Credential</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Credential Type *</Label>
              <Select
                value={newCredential.credential_type}
                onValueChange={(val) => setNewCredential({ ...newCredential, credential_type: val })}
              >
                <SelectTrigger data-testid="credential-type-select">
                  <SelectValue placeholder="Select credential type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="OSHA 10-Hour">OSHA 10-Hour</SelectItem>
                  <SelectItem value="OSHA 30-Hour">OSHA 30-Hour</SelectItem>
                  <SelectItem value="Union Membership">Union Membership</SelectItem>
                  <SelectItem value="Professional Association">Professional Association</SelectItem>
                  <SelectItem value="Safety Certification">Safety Certification</SelectItem>
                  <SelectItem value="Equipment Operator Certification">Equipment Operator Certification</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Credential Number/ID</Label>
              <Input
                value={newCredential.credential_number}
                onChange={(e) => setNewCredential({ ...newCredential, credential_number: e.target.value })}
                placeholder="e.g., MEMBER-12345"
                data-testid="credential-number-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={newCredential.issue_date}
                  onChange={(e) => setNewCredential({ ...newCredential, issue_date: e.target.value })}
                  data-testid="credential-issue-date-input"
                />
              </div>
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={newCredential.expiry_date}
                  onChange={(e) => setNewCredential({ ...newCredential, expiry_date: e.target.value })}
                  data-testid="credential-expiry-date-input"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newCredential.notes}
                onChange={(e) => setNewCredential({ ...newCredential, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
                data-testid="credential-notes-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAddCredentialDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={addMyCredential} 
                disabled={!newCredential.credential_type}
                data-testid="save-credential-btn"
              >
                Save Credential
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Personal Details Dialog */}
      <PersonalDetailsDialog 
        open={showPersonalDetailsDialog}
        onOpenChange={setShowPersonalDetailsDialog}
        user={user}
        onUpdate={fetchData}
      />

      {/* Driving License Prompt */}
      <DrivingLicensePrompt user={user} onComplete={fetchMyProfile} />

      {/* Help Center Dialog */}
      <Dialog open={showHelpCenter} onOpenChange={setShowHelpCenter}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <HelpCenter onClose={() => setShowHelpCenter(false)} />
        </DialogContent>
      </Dialog>

      {/* Onboarding Tour (shows on first login) */}
      <OnboardingTour user={user} />
    </div>
  );
}
